import { Link } from "react-router-dom";
import {
  DollarSign, Clock, ShieldCheck, Home as HomeIcon, Handshake,
  FileText, MapPin, ArrowRight, CheckCircle2,
} from "lucide-react";
import DOMPurify from "dompurify";
import { useSEO } from "@/hooks/useSEO";

const SERVICES = [
  {
    icon: DollarSign,
    title: "Cash Offers on Any House",
    body:
      "We buy houses in any condition. Inherited properties, tired rentals, pre-foreclosure, divorce, code violations, hurricane damage. No repairs required. No cleaning. We handle it all.",
    bullets: [
      "Written offer in 24 hours",
      "Backed by real neighborhood comps",
      "Never renegotiated at close",
    ],
  },
  {
    icon: Clock,
    title: "Fast Closings",
    body:
      "Need out in two weeks? We can close in 14 days when title runs clean. Not in a rush? Pick any date up to 90 days out. Your timeline dictates ours. Never the other way around.",
    bullets: [
      "14-day close available",
      "Wire funds day of closing",
      "Remote sign option",
    ],
  },
  {
    icon: ShieldCheck,
    title: "As-Is Purchases",
    body:
      "Leave the house exactly as it stands. Trash, tenant belongings, storm damage, broken systems. We take responsibility for everything after closing.",
    bullets: [
      "No repairs or cleaning",
      "Bring only what you want",
      "We handle disposal & haul-off",
    ],
  },
  {
    icon: Handshake,
    title: "Transparent Pricing",
    body:
      "Every number in your offer is backed by data we share with you. Recent sold comps, our repair estimate, and the after-repair value we're targeting.",
    bullets: [
      "Comp report included",
      "Line-by-line breakdown",
      "No hidden fees or deductions",
    ],
  },
  {
    icon: FileText,
    title: "Full-Service Paperwork",
    body:
      "We handle title, closing statements, deed, and every legal document. You show up (in person or remotely) and sign. Everything else is done for you.",
    bullets: [
      "Preferred local title company",
      "Electronic signing available",
      "Same-day funding",
    ],
  },
  {
    icon: HomeIcon,
    title: "Every Situation",
    body:
      "Inherited without a will? Behind on taxes? Facing foreclosure? Split with a spouse? Job relocation? We've bought a house in every scenario you can name.",
    bullets: [
      "Inherited &amp; probate properties",
      "Pre-foreclosure &amp; short sale",
      "Divorce &amp; estate liquidation",
    ],
  },
];

const AGENT_VS_US = [
  { label: "Time to close",         agent: "3–6 months",       us: "7–30 days" },
  { label: "Repairs required",      agent: "Yes, before listing", us: "None. As-is." },
  { label: "Showings & open houses",agent: "Weekends of hosting", us: "None." },
  { label: "Agent commissions",     agent: "5–6% of sale price",  us: "Zero." },
  { label: "Closing costs",         agent: "1–3% on you",         us: "Zero on you." },
  { label: "Offer certainty",       agent: "Buyer can back out", us: "Signed contract, guaranteed close." },
];

export default function Services() {
  useSEO({
    title: "Services. Cash for Houses in Southwest Florida | FlipPilot",
    description:
      "We buy houses for cash in Cape Coral, Fort Myers, Naples, and all of Southwest Florida. Any condition, any situation. Close in 7–30 days with zero fees and zero repairs.",
    path: "/services",
    keywords:
      "cash for houses Cape Coral, sell house fast Fort Myers, we buy houses Naples, as-is home sale Florida, sell inherited house SWFL",
  });

  return (
    <>
      {/* Header */}
      <section className="pt-16 pb-12" data-testid="services-hero">
        <div className="mkt-container">
          <span className="mkt-chip">
            <MapPin size={12} strokeWidth={2.5} />
            Southwest Florida
          </span>
          <h1 className="mkt-hero-title mt-6 max-w-4xl mkt-reveal">
            Everything included. <br />
            <span style={{ color: "var(--mkt-accent)" }}>Nothing hidden.</span>
          </h1>
          <p className="mt-5 text-lg md:text-xl text-[var(--mkt-text-muted)] max-w-3xl">
            One fair offer. One closing date. Zero fees. Here&rsquo;s exactly
            what you get when you sell your house to FlipPilot.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[var(--mkt-surface)]" data-testid="services-sellers">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ What we deliver</span>
          <h2 className="mkt-section-title mt-3 max-w-3xl">
            The stress-free way to sell your house.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {SERVICES.map(({ icon: Icon, title, body, bullets }, i) => (
              <div key={title} className="mkt-card p-6" data-testid={`seller-service-${i}`}>
                <div
                  className="w-11 h-11 grid place-items-center rounded-full"
                  style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
                >
                  <Icon size={18} strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold mt-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
                <p className="text-sm text-[var(--mkt-text-muted)] mt-2 leading-relaxed">
                  {body}
                </p>
                <ul className="mt-4 space-y-1.5">
                  {bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--mkt-accent)" }} />
                      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b) }} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agent vs Us comparison. The closer */}
      <section className="py-20" data-testid="services-comparison">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ Selling to us vs. An agent</span>
          <h2 className="mkt-section-title mt-3 max-w-3xl">
            The math is simpler than you think.
          </h2>
          <p className="mt-4 text-[var(--mkt-text-muted)] max-w-2xl">
            The retail sale price looks bigger on paper. Until you subtract
            three to six months of holding costs, agent commissions, closing
            fees, repair credits, and the risk of the deal falling through.
          </p>

          <div className="mkt-card overflow-hidden mt-10">
            <div className="grid grid-cols-3 text-xs uppercase tracking-widest border-b border-[var(--mkt-line)]">
              <div className="p-4"></div>
              <div className="p-4 text-[var(--mkt-text-muted)]">Traditional Agent</div>
              <div
                className="p-4 font-semibold"
                style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
              >
                FlipPilot
              </div>
            </div>
            {AGENT_VS_US.map((row, i) => (
              <div
                key={row.label}
                className="grid grid-cols-3 text-sm border-b border-[var(--mkt-line)] last:border-b-0"
                data-testid={`compare-row-${i}`}
              >
                <div className="p-4 font-medium">{row.label}</div>
                <div className="p-4 text-[var(--mkt-text-muted)]">{row.agent}</div>
                <div
                  className="p-4 font-semibold"
                  style={{ background: "rgba(14,165,233,0.05)", color: "var(--mkt-accent-dark)" }}
                >
                  {row.us}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact" className="mkt-btn mkt-btn-primary">
              Request My Cash Offer
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
